import type { StorefrontConfig } from './schema'
import type { Tokens } from './tokens'

/**
 * Project a storefront's authored branding onto the tokens derived from its
 * stored palette. The palette is the inherited baseline; `branding` is the
 * narrow set of overrides the new config exposes.
 *
 * The plan puts this inside `packages/web/src/config/loadConfig.ts`. It lives
 * here instead: it is a pure `(Tokens, StorefrontConfig) => Tokens` projection
 * with nothing web-specific in it, so leaving it in `web` would mean the React
 * Native shell reimplements branding — exactly the rewrite this architecture
 * exists to avoid. Task 8's `loadConfig` imports it.
 */
export const applyBrandingOverrides = (
  tokens: Tokens,
  config: StorefrontConfig,
): Tokens => {
  const b = config.branding
  return {
    ...tokens,
    mode: b.mode ?? tokens.mode,
    logoUrl: config.logoUrl || tokens.logoUrl,
    font: { ...tokens.font, family: b.fontFamily ?? tokens.font.family },
    // `radius: 0` is a legitimate "square corners" choice, so this is a
    // null-ish check rather than a falsy one. Only `md` is authorable because
    // it is the single value MUI's `shape.borderRadius` consumes.
    radius: b.radius != null ? { ...tokens.radius, md: b.radius } : tokens.radius,
    color: {
      ...tokens.color,
      primary: b.primary ?? tokens.color.primary,
      secondary: b.secondary ?? tokens.color.secondary,
      surface: b.surface ?? tokens.color.surface,
      background: b.background ?? tokens.color.background,
    },
  }
}
